import { Button, createCssVariableString, useBreakpoints } from "best-common-react";
import { Link } from "react-router";
import React, { useMemo } from "react";
import styled from "styled-components";
import RouteConstants from "../../../constants/RouteConstants";
import UserAllotmentConstants from "../../../constants/UserAllotmentConstants";
import { useAuth } from "../../../contexts/AuthContext";
import { EventTimingDTO, EventTimingTypeDTO } from "../../../types/EventTiming";
import { GameRequestCountDTO } from "../../../types/Game";
import { Offset, TimeUntil, useTimeUntil } from "../../../util/TimeUtil";
import TimeRemaining from "./TimeRemaining";
import { RequestTypeCountDTO } from "../../../types/RequestType";
import clsx from "clsx";

const CountInfo = styled.div`
  color: ${(props) => props.color};
  font-size: var(--font-size-small);
  font-style: italic;
`;

const APPROVED = "approved";
const DENIED = "denied";
const PENDING = "pending";

const getColor = (key: string) => {
  switch (key) {
    case APPROVED:
      return createCssVariableString("coreColorsOnSurfaceGreen");
    case PENDING:
      return createCssVariableString("coreColorsOnSurfaceBlue");
    case DENIED:
      return createCssVariableString("coreColorsOnSurfaceRed");
    default:
      return createCssVariableString("coreColorsBlack");
  }
};

const hasCount = (value: number) => value && value > 0;

const getStatusId = (category: string) => {
  switch (category) {
    case PENDING:
      return "1,2";
    case APPROVED:
      return "3";
    case DENIED:
      return "4";
    default:
      return "";
  }
};

const Count = ({ dataKey, data, fullWord, category, gamePk }) => {
  const color = getColor(dataKey);
  const value = data ? data[dataKey] : 0;
  const statusId = getStatusId(dataKey);
  const search = `?category=${category}&status=${statusId}&gamePk=${gamePk}`;
  return hasCount(value) ? (
    <div className="bcr-ms-1">
      <Link
        to={{
          pathname: RouteConstants.REQUESTS.BASE,
          search: search,
        }}
      >
        <CountInfo color={color}>{fullWord ? `${value} ${dataKey}` : `(${value})`}</CountInfo>
      </Link>
    </div>
  ) : null;
};

type RequestCountsProps = {
  requestCount?: RequestTypeCountDTO;
  category: number;
  gamePk: string | number;
};

const RequestCounts = ({ requestCount, category, gamePk }: RequestCountsProps) => {
  let typeCount = 0;
  if (!!requestCount) {
    if (hasCount(requestCount[PENDING])) {
      typeCount++;
    }
    if (hasCount(requestCount[APPROVED])) {
      typeCount++;
    }
    if (hasCount(requestCount[DENIED])) {
      typeCount++;
    }
  }
  const fullWord = typeCount <= 1;

  return (
    <div className="d-flex justify-content-between">
      <div className="d-flex">
        <Count dataKey={PENDING} data={requestCount} fullWord={fullWord} category={category} gamePk={gamePk} />
        <Count dataKey={APPROVED} data={requestCount} fullWord={fullWord} category={category} gamePk={gamePk} />
        <Count dataKey={DENIED} data={requestCount} fullWord={fullWord} category={category} gamePk={gamePk} />
      </div>
    </div>
  );
};

type TicketRequestSectionProps = {
  gameDate: string;
  access: EventTimingTypeDTO;
  cutoff: EventTimingDTO;
  toRequest: (type: string) => void;
  blackedOut?: boolean;
  personalAllowed: boolean;
  requestCount: GameRequestCountDTO;
  gamePk: string | number;
};

type TicketRequestSectionDisplayProps = {
  isPersonal: boolean;
  active: boolean;
  requestCount: RequestTypeCountDTO;
  gamePk: string | number;
  offset: Offset;
  toRequest: (type: string) => void;
};

const TicketRequestSectionDisplay = ({
  isPersonal,
  active,
  requestCount,
  gamePk,
  offset,
  toRequest,
}: TicketRequestSectionDisplayProps) => {
  return (
    <div className="d-flex align-items-start bcr-gap-2">
      <div className="d-flex flex-column bcr-gap-2">
        <Button
          variant={isPersonal ? "primary" : "secondary"}
          disabled={!active}
          onClick={() => toRequest(UserAllotmentConstants.PERSONAL)}
          styles={{
            button: {
              height: "2rem",
            },
          }}
        >
          {isPersonal ? "Personal" : "Business"}
        </Button>
        <RequestCounts requestCount={requestCount} gamePk={gamePk} category={isPersonal ? 1 : 2} />
      </div>
      <TimeRemaining offset={offset} isPersonal={true} />
    </div>
  );
};

const TicketRequestSection = ({
  gameDate,
  access,
  cutoff,
  toRequest,
  blackedOut = false,
  personalAllowed,
  requestCount,
  gamePk,
}: TicketRequestSectionProps) => {
  const { isAdmin } = useAuth();
  const { isMobile } = useBreakpoints();
  const personalCutoff: TimeUntil = useTimeUntil(gameDate, access, cutoff.personal);
  const businessCutoff: TimeUntil = useTimeUntil(gameDate, access, cutoff.business);
  const businessActive: boolean = useMemo(() => {
    return !blackedOut && (businessCutoff.accessible || isAdmin);
  }, [blackedOut, businessCutoff.accessible, isAdmin]);
  const personalActive: boolean = useMemo(() => {
    return !blackedOut && ((personalCutoff.accessible && personalAllowed) || isAdmin);
  }, [blackedOut, personalCutoff.accessible, personalAllowed, isAdmin]);

  return (
    <div className={clsx("d-flex align-items-center", { "w-100": isMobile })}>
      <div className={clsx("d-flex", { "bcr-gap-3": !isMobile, "justify-content-between w-100 bcr-mt-2": isMobile })}>
        <TicketRequestSectionDisplay
          isPersonal={true}
          active={personalActive}
          requestCount={requestCount?.personal}
          gamePk={gamePk}
          offset={personalCutoff.offset}
          toRequest={toRequest}
        />
        <TicketRequestSectionDisplay
          isPersonal={false}
          active={businessActive}
          requestCount={requestCount?.business}
          gamePk={gamePk}
          offset={businessCutoff.offset}
          toRequest={toRequest}
        />
      </div>
    </div>
  );
};

export default TicketRequestSection;
